import { gql, type TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { InputFormField } from '@repo/shared-client/components/fields'
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Spinner,
} from '@repo/shared-client/components/ui'
import { Form } from '@repo/shared-client/form'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from '@web/src/lib/intl/react'
import { InfoIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { EventInfo } from 'src/lib/fragments'
import type { CloneEventMutation, CloneEventMutationVariables } from 'types/graphql'

const CLONE_EVENT_MUTATION: TypedDocumentNode<CloneEventMutation, CloneEventMutationVariables> = gql`
  mutation CloneEventMutation($eventIdOrSlug: String!, $includeMedia: Boolean!, $newName: String!) {
    cloneEvent(eventIdOrSlug: $eventIdOrSlug, includeMedia: $includeMedia, newName: $newName) {
      ...EventInfo
    }
  }
  ${EventInfo}
`

type EventCloneModalProps = {
  open?: boolean
  onOpenChange?: (details: { open: boolean }) => void
  eventIdOrSlug?: string
  eventName?: string
}

type CloneEventFormValues = {
  eventName: string
}

const EventCloneModal = ({ eventIdOrSlug, eventName, open, onOpenChange }: EventCloneModalProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const initialRef = useRef<HTMLInputElement>(null)
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const [includeMedia, setIncludeMedia] = useState(true)

  const formMethods = useForm<CloneEventFormValues>({
    defaultValues: { eventName: '' },
  })
  const { reset, watch } = formMethods
  const eventNameValue = watch('eventName')

  useEffect(() => {
    if (eventName) {
      reset({ eventName: `CLONED - ${eventName}` })
    }
  }, [eventName, reset])

  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => initialRef.current?.focus())
      return () => cancelAnimationFrame(id)
    }
  }, [open])

  const [cloneEvent] = useMutation(CLONE_EVENT_MUTATION, {
    onCompleted: (data) => {
      toast.success(t('Event cloned successfully'))
      setShowSpinnerValue(false)
      closeDialog()

      if (data?.cloneEvent?.id) {
        navigate({
          to: '/event/$eventIdOrSlug/edit',
          params: { eventIdOrSlug: data.cloneEvent.slug || data.cloneEvent.id },
        } as never)
      }
    },
    onError: (error) => {
      toast.error(error.message)
      setShowSpinnerValue(false)
    },
  })

  const onSubmit: SubmitHandler<CloneEventFormValues> = ({ eventName: name }) => {
    const trimmed = name.trim()
    if (!trimmed) {
      toast.error(t('Event name cannot be empty'))
      return
    }

    setShowSpinnerValue(true)
    cloneEvent({
      variables: {
        eventIdOrSlug,
        includeMedia,
        newName: trimmed,
      },
    })
  }

  const closeDialog = () => {
    onOpenChange?.({ open: false })
  }

  return (
    <Dialog
      disablePointerDismissal
      onOpenChange={(nextOpen, eventDetails) => {
        if (
          nextOpen === false &&
          eventDetails &&
          typeof eventDetails === 'object' &&
          'reason' in eventDetails &&
          eventDetails.reason === 'escape-key' &&
          'cancel' in eventDetails &&
          typeof eventDetails.cancel === 'function'
        ) {
          eventDetails.cancel()
          return
        }
        onOpenChange?.({ open: nextOpen })
      }}
      open={open}
    >
      <DialogContent className="max-w-md sm:max-w-lg" initialFocus={false} showCloseButton={!showSpinner}>
        <DialogHeader>
          <DialogTitle>{t('Clone Event')}</DialogTitle>
        </DialogHeader>

        <Form className="contents" formMethods={formMethods} onSubmit={onSubmit}>
          <div className="flex flex-col gap-4 pb-2">
            <Alert>
              <InfoIcon />
              <AlertDescription>
                {t('This will create a copy of the event with all its details. You can customize the name below.')}
              </AlertDescription>
            </Alert>

            <InputFormField
              label={t('New Event Name')}
              name="eventName"
              placeholder={t('Enter event name')}
              ref={initialRef}
              required
            />

            <div className="flex w-full items-center justify-center gap-2 text-base">
              <Checkbox
                checked={includeMedia}
                id="event-clone-include-media"
                onCheckedChange={(checked) => setIncludeMedia(Boolean(checked))}
              />
              <Label className="cursor-pointer font-normal" htmlFor="event-clone-include-media">
                {t('Include Media')}
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button disabled={showSpinner} onClick={closeDialog} type="button" variant="outline">
              {t('Cancel')}
            </Button>
            <Button disabled={!eventNameValue?.trim() || showSpinner} type="submit">
              {showSpinner ? <Spinner className="size-4" /> : null}
              {t('Clone Event')}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default EventCloneModal
